/**
 * HeatSourceParamsFields — pola formularza źródła ciepła z tabletu (lokalizacja + parametry typu, opcjonalnie notatka
 * wspólnego źródła) przez lustro utils/heatSourceParams (parytet: backend/test/heatSourceParamsParity.test.js).
 *
 * Jeden renderer dla obu miejsc edycji w panelu: wspólne źródło budynku (HeatSourceEditModal, B8 2026-10-19) i źródło
 * LOKALU w formularzu lokalu (ApartmentHeatSourcesSection — zadanie 4, 2026-10-21: przegląd z papieru wprowadzany
 * w panelu „tak jakby był zrobiony na tablecie"). Komponent tylko rysuje pola i zgłasza zmianę wartości — co zapisać
 * (wyłącznie pola zmienione względem odczytu), decyduje wywołujący (heatParamChanges).
 */
import React from 'react';
import { InputField } from '../ds/InputField';
import { ROOM_OPTIONS } from './RecommendationDetails/PgCogDetails';
import {
  LOCATION_FIELD_LABEL,
  LOCATION_OTHER_FIELD_LABEL,
  LOCATION_OTHER_OPTION_LABEL,
  LOCATION_OTHER_VALUE,
  NOTE_FIELD_HINT,
  NOTE_FIELD_LABEL,
  ParamField,
  heatSourceParamFields,
  installYearError,
  isParamFieldVisible,
} from '../utils/heatSourceParams';

interface Props {
  /** Prefiks id pól (unikalny w dokumencie — etykiety <label htmlFor>). */
  idPrefix: string;
  type: string;
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  disabled?: boolean;
  /** Notatka wspólnego źródła budynku (`params.notatka`) — tylko wariant budynku, jak na tablecie. */
  withNote?: boolean;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 'var(--space-2)',
  fontSize: 'var(--text-label)',
  fontWeight: 'var(--weight-semibold)',
  color: 'var(--text-secondary)',
};

export const heatSourceSelectStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 'var(--touch-min)',
  padding: '0 var(--space-4)',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-body)',
  color: 'var(--text-primary)',
  background: 'var(--surface-card)',
  border: 'var(--border-width-strong) solid var(--border-strong)',
  borderRadius: 'var(--radius-md)',
};

const isBlank = (v: unknown) => v === undefined || v === null || v === '';

export function HeatSourceParamsFields({ idPrefix, type, values, onChange, disabled = false, withNote = false }: Props) {
  const fields = heatSourceParamFields(type);

  const renderSelect = (id: string, label: string, key: string, options: { value: string; label: string }[], clearable: boolean) => {
    const current = isBlank(values[key]) ? '' : String(values[key]);
    // Wartość spoza słownika (np. surowy tekst z importu CEEB) zostaje widoczna i nietknięta, dopóki admin jej nie zmieni.
    const outside = current !== '' && !options.some((o) => o.value === current);
    return (
      <div key={key}>
        <label htmlFor={id} style={labelStyle}>{label}</label>
        <select id={id} value={current} disabled={disabled} onChange={(e) => onChange(key, e.target.value)} style={heatSourceSelectStyle}>
          {(clearable || current === '') && <option value="">— nie wybrano —</option>}
          {outside && <option value={current}>{current} (spoza listy)</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  };

  const renderField = (field: ParamField) => {
    const id = `${idPrefix}-${field.key}`;
    if (field.type === 'checkbox') {
      const checked = values[field.key] === true || values[field.key] === 'true';
      return (
        <label key={field.key} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(field.key, e.target.checked)} />
          <span>{field.label}</span>
        </label>
      );
    }
    if (field.type === 'select') {
      // Pola z defaultem Bronka (CEEB ich wymaga) nie dają się wyczyścić — jak na tablecie.
      return renderSelect(id, field.label, field.key, field.options || [], false);
    }
    const current = isBlank(values[field.key]) ? '' : String(values[field.key]);
    return (
      <InputField
        key={field.key}
        id={id}
        label={field.label}
        value={current}
        disabled={disabled}
        inputMode={field.type === 'number' ? 'decimal' : undefined}
        errorMessage={field.key === 'yearInstalled' ? installYearError(current) ?? undefined : undefined}
        // Jak tablet: przecinek dziesiętny zapisujemy kropką.
        onChange={(e) => onChange(field.key, field.type === 'number' ? e.target.value.replace(',', '.') : e.target.value)}
      />
    );
  };

  const locationOptions = [
    ...ROOM_OPTIONS.map((r) => ({ value: r.id, label: r.label })),
    { value: LOCATION_OTHER_VALUE, label: LOCATION_OTHER_OPTION_LABEL },
  ];

  return (
    <>
      {renderSelect(`${idPrefix}-lokalizacja`, LOCATION_FIELD_LABEL, 'lokalizacja', locationOptions, true)}
      {values.lokalizacja === LOCATION_OTHER_VALUE && (
        <InputField
          id={`${idPrefix}-lokalizacjaInna`}
          label={LOCATION_OTHER_FIELD_LABEL}
          value={isBlank(values.lokalizacjaInna) ? '' : String(values.lokalizacjaInna)}
          disabled={disabled}
          onChange={(e) => onChange('lokalizacjaInna', e.target.value)}
        />
      )}

      {fields.filter((f) => isParamFieldVisible(f, values)).map(renderField)}

      {withNote && (
        <InputField
          id={`${idPrefix}-notatka`}
          multiline
          rows={3}
          label={NOTE_FIELD_LABEL}
          hint={NOTE_FIELD_HINT}
          value={isBlank(values.notatka) ? '' : String(values.notatka)}
          disabled={disabled}
          onChange={(e) => onChange('notatka', e.target.value)}
        />
      )}
    </>
  );
}
